import api from "../../../services/api";
import { FileUploadInfo } from "./BlocoAdicionaAnexo";
import { truncarNomeArquivo } from "../../../utility/byteSizeUtils";

export const SUCCESS_REMOVAL_DELAY = 3000; // 3 seconds

interface UploadFileParams {
    file: File;
    index: number;
    demandaId?: number;
    onProgress: (index: number, progress: number) => void;
    onSuccess: (index: number, responseData?: any) => void;
    onError: (index: number, error: string) => void;
}

interface UploadMultipleFilesParams {
    files: File[];
    indexes: number[];
    demandaId?: number;
    onProgress: (index: number, progress: number) => void;
    onSuccess: (index: number, responseData?: any) => void;
    onError: (index: number, error: string) => void;
}

export const uploadFile = async ({
    file,
    index,
    demandaId,
    onProgress,
    onSuccess,
    onError
}: UploadFileParams): Promise<{ success: boolean; file: File; data?: any }> => {
    const formData = new FormData();
    formData.append('arquivo', file);

    try {
        const response = await api.post(
            `proposta/${demandaId}/anexos`, 
            formData,
            {
                onUploadProgress: (progressEvent: any) => {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(index, percent);
                }
            }
        );

        if (response.data.status !== 'success') {
            throw new Error(response.data.message);
        }
        
        onSuccess(index, response.data);
        return { success: true, file, data: response.data };
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || 
                            error?.response?.data?.details || 
                            error?.response?.data?.error || 
                            error?.message || 
                            'Erro ao enviar arquivo';
        onError(index, errorMessage);
        return { success: false, file };
    }
};

export const uploadMultipleFiles = async ({
    files,
    indexes,
    demandaId,
    onProgress,
    onSuccess,
    onError
}: UploadMultipleFilesParams): Promise<{ success: boolean; partialSuccess?: boolean; results: Array<{ success: boolean; file: File; data?: any }> }> => {
    if (!files.length || !indexes.length || files.length !== indexes.length) {
        return { success: false, results: [] };
    }

    const formData = new FormData();
    
    // Adiciona todos os arquivos ao FormData com o mesmo nome de campo (array)
    files.forEach(file => {
        formData.append('arquivos', file);
    });

    try {
        let response;
        try {
            response = await api.post(
                `proposta/${demandaId}/anexos-multi`,
                formData,
                {
                    onUploadProgress: (progressEvent: any) => {
                        if (!progressEvent.total) return;
                        
                        // Distribui o progresso igualmente entre todos os arquivos
                        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        indexes.forEach(idx => {
                            onProgress(idx, percent);
                        });
                    }
                }
            );
        } catch (axiosError: any) {
            // Se o axios lançou erro mas há uma resposta com dados de falhas, usa a resposta
            if (axiosError?.response?.data && 
                (axiosError.response.data.falhas || axiosError.response.data.sucessos)) {
                response = axiosError.response;
            } else {
                throw axiosError;
            }
        }

        // Verifica status da resposta
        // Aceita 'success', 'partial_success' e 'error' (quando há sucessos e falhas)
        const status = response.data.status;
        const temSucessos = (response.data.sucessos || response.data.resultados || []).length > 0;
        const temFalhas = (response.data.falhas || []).length > 0;
        
        // Se for 'error' mas não houver sucessos nem falhas, lança erro genérico
        if (status === 'error' && !temSucessos && !temFalhas) {
            throw new Error(response.data.message || 'Erro ao enviar arquivos');
        }
        
        const results: Array<{ success: boolean; file: File; data?: any }> = [];
        
        // Processa resultados individuais
        // Trata 'partial_success' e 'error' quando há sucessos e/ou falhas
        if (status === 'partial_success' || (status === 'error' && (temSucessos || temFalhas))) {
            // Alguns arquivos tiveram sucesso, outros falharam
            const sucessos = response.data.sucessos || [];
            const falhas = response.data.falhas || [];
            const resultadosUsados = new Set();
            
            // Função auxiliar para normalizar nomes para comparação
            const normalizarNome = (nome: string): string => {
                return nome.toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/[^a-z0-9.]/g, '');
            };
            
            files.forEach((file, idx) => {
                const fileIndex = indexes[idx];
                const fileNome = file.name;
                const fileNomeNormalizado = normalizarNome(fileNome);
                
                // Tenta encontrar o arquivo na lista de sucessos
                let sucessoItem = sucessos.find((s: any) => {
                    if (resultadosUsados.has(s)) return false;
                    if (s.nome_original === fileNome) return true;
                    if (s.nome_original && normalizarNome(s.nome_original) === fileNomeNormalizado) return true;
                    if (s.caminho) {
                        const nomeDoCaminho = s.caminho.split('/').pop() || '';
                        if (normalizarNome(nomeDoCaminho) === fileNomeNormalizado) return true;
                    }
                    return false;
                });
                
                if (sucessoItem) {
                    resultadosUsados.add(sucessoItem);
                    onSuccess(fileIndex, { result: sucessoItem });
                    results.push({ success: true, file, data: { result: sucessoItem } });
                } else {
                    // Verifica se está na lista de falhas
                    let falhaItem = falhas.find((f: any) => {
                        if (f.nome_original === fileNome) return true;
                        if (f.nome_original && normalizarNome(f.nome_original) === fileNomeNormalizado) return true;
                        return false;
                    });
                    
                    const errorMessage = falhaItem?.mensagem || `Arquivo "${truncarNomeArquivo(fileNome)}" não foi processado pelo servidor`;
                    onError(fileIndex, errorMessage);
                    results.push({ success: false, file });
                }
            });
            
            return { success: true, partialSuccess: true, results };
        } else {
            // Todos os arquivos tiveram sucesso (segundo o backend)
            const resultados = response.data.resultados || [];
            const resultadosUsados = new Set();
            
            // Função auxiliar para normalizar nomes para comparação
            const normalizarNome = (nome: string): string => {
                return nome.toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/[^a-z0-9.]/g, '');
            };
            
            // Detecta se há discrepância entre arquivos enviados e resultados
            const haDiscrepancia = resultados.length !== files.length;
            
            if (haDiscrepancia) {
                console.warn(`⚠️ Número de resultados (${resultados.length}) não corresponde ao número de arquivos (${files.length})`);
            }
            
            files.forEach((file, idx) => {
                const fileIndex = indexes[idx];
                const fileNome = file.name;
                const fileNomeNormalizado = normalizarNome(fileNome);
                
                // Tenta encontrar o resultado de múltiplas formas
                let resultado = resultados.find((r: any) => {
                    // Evita usar resultados já associados
                    if (resultadosUsados.has(r)) return false;
                    
                    // Compara por nome_original exato
                    if (r.nome_original === fileNome) return true;
                    
                    // Compara por nome_original normalizado
                    if (r.nome_original && normalizarNome(r.nome_original) === fileNomeNormalizado) return true;
                    
                    // Tenta extrair do caminho (formato: "id_proposta/nome_arquivo.ext")
                    if (r.caminho) {
                        const nomeDoCaminho = r.caminho.split('/').pop() || '';
                        if (normalizarNome(nomeDoCaminho) === fileNomeNormalizado) return true;
                    }
                    
                    return false;
                });
                
                if (resultado) {
                    resultadosUsados.add(resultado);
                    onSuccess(fileIndex, { result: resultado });
                    results.push({ success: true, file, data: { result: resultado } });
                } else {
                    // Não encontrou correspondência por nome
                    // Só usa fallback por índice se NÃO houver discrepância conhecida
                    // e se ainda houver resultados disponíveis
                    if (!haDiscrepancia && idx < resultados.length) {
                        const resultadoPorIndice = resultados[idx];
                        if (resultadoPorIndice && !resultadosUsados.has(resultadoPorIndice)) {
                            resultadosUsados.add(resultadoPorIndice);
                            onSuccess(fileIndex, { result: resultadoPorIndice });
                            results.push({ success: true, file, data: { result: resultadoPorIndice } });
                        } else {
                            // Não encontrou correspondência - marca como erro
                            const errorMsg = `Arquivo "${truncarNomeArquivo(fileNome)}" não foi encontrado na resposta do servidor`;
                            onError(fileIndex, errorMsg);
                            results.push({ success: false, file });
                        }
                    } else {
                        // Há discrepância ou não há resultado suficiente - marca como erro
                        // Nota: O backend pode estar processando os arquivos assincronamente
                        // e a resposta pode ser enviada antes de todos serem processados.
                        // Os arquivos podem aparecer após recarregar a página.
                        const errorMsg = haDiscrepancia 
                            ? `Arquivo "${truncarNomeArquivo(fileNome)}" não foi retornado na resposta (${resultados.length} de ${files.length} processados). Pode aparecer após recarregar.`
                            : `Arquivo "${truncarNomeArquivo(fileNome)}" não foi processado pelo servidor`;
                        onError(fileIndex, errorMsg);
                        results.push({ success: false, file });
                    }
                }
            });
            
            // Verifica se todos os arquivos foram mapeados corretamente
            const sucessos = results.filter(r => r.success).length;
            const falhas = results.filter(r => !r.success).length;
            
            // Se há falhas ou discrepância, retorna partialSuccess mesmo que o status seja 'success'
            if (falhas > 0 || haDiscrepancia) {
                return { 
                    success: true, 
                    partialSuccess: true, 
                    results 
                };
            }
            
            return { success: true, results };
        }
    } catch (error: any) {
        // Verifica se o erro tem uma resposta com dados de falhas específicas
        if (error?.response?.data?.falhas && Array.isArray(error.response.data.falhas)) {
            const falhas = error.response.data.falhas;
            const results: Array<{ success: boolean; file: File; data?: any }> = [];
            
            const normalizarNome = (nome: string): string => {
                return nome.toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/[^a-z0-9.]/g, '');
            };
            
            files.forEach((file, idx) => {
                const fileIndex = indexes[idx];
                const fileNome = file.name;
                const fileNomeNormalizado = normalizarNome(fileNome);
                
                const falhaItem = falhas.find((f: any) => {
                    if (f.nome_original === fileNome) return true;
                    if (f.nome_original && normalizarNome(f.nome_original) === fileNomeNormalizado) return true;
                    return false;
                });
                
                const errorMessage = falhaItem?.mensagem || 
                                    error?.response?.data?.message || 
                                    error?.message || 
                                    'Erro ao enviar arquivos';
                onError(fileIndex, errorMessage);
                results.push({ success: false, file });
            });
            
            return { 
                success: false, 
                results 
            };
        }
        
        // Em caso de erro geral sem falhas específicas, marca todos os arquivos como falha
        const errorMessage = error?.response?.data?.message || 
                            error?.response?.data?.details || 
                            error?.response?.data?.error || 
                            error?.message || 
                            'Erro ao enviar arquivos';
        
        files.forEach((file, idx) => {
            const fileIndex = indexes[idx];
            onError(fileIndex, errorMessage);
        });
        
        return { 
            success: false, 
            results: files.map(file => ({ success: false, file }))
        };
    }
};

interface UploadWithStateManagementParams {
    file: File;
    index: number;
    demandaId?: number;
    setUploadingFiles: React.Dispatch<React.SetStateAction<FileUploadInfo[]>>;
    onSuccessCallback?: (responseData?: any) => void;
    removalDelay?: number;
}

interface UploadMultipleWithStateManagementParams {
    files: File[];
    indexes: number[];
    demandaId?: number;
    setUploadingFiles: React.Dispatch<React.SetStateAction<FileUploadInfo[]>>;
    onSuccessCallback?: (responseData?: any) => void;
    removalDelay?: number;
}

export const uploadFileWithStateManagement = async ({
    file,
    index,
    demandaId,
    setUploadingFiles,
    onSuccessCallback,
    removalDelay = SUCCESS_REMOVAL_DELAY
}: UploadWithStateManagementParams): Promise<{ success: boolean; file: File; data?: any }> => {
    return uploadFile({
        file,
        index,
        demandaId,
        onProgress: (idx, percent) => {
            setUploadingFiles(prev => 
                prev.map((f, i) => 
                    i === idx ? { ...f, progress: percent, progresso: percent } : f
                )
            );
        },
        onSuccess: (idx, responseData) => {
            setUploadingFiles(prev => 
                prev.map((f, i) => {
                    if (i !== idx) return f;
                    return {
                        ...f,
                        status: 'success',
                        progress: 100,
                        progresso: 100,
                        erro: undefined
                    };
                })
            );
            // Remove the card and call the callback when removed
            removeFileByIndex(setUploadingFiles, idx, removalDelay, () => {
                onSuccessCallback?.(responseData);
            }, file);
        },
        onError: (idx, errorMessage) => {
            setUploadingFiles(prev => 
                prev.map((f, i) => {
                    if (i !== idx) return f;
                    return { 
                        ...f, 
                        status: 'error', 
                        error: errorMessage,
                        erro: errorMessage
                    };
                })
            );
        }
    });
};

export const uploadMultipleFilesWithStateManagement = async ({
    files,
    indexes,
    demandaId,
    setUploadingFiles,
    onSuccessCallback,
    removalDelay = SUCCESS_REMOVAL_DELAY
}: UploadMultipleWithStateManagementParams): Promise<{ success: boolean; partialSuccess?: boolean; results: Array<{ success: boolean; file: File; data?: any }> }> => {
    if (!files.length || !indexes.length || files.length !== indexes.length) {
        return { success: false, results: [] };
    }

    const arquivosPorIndice = new Map<number, File>();
    indexes.forEach((indice, posicao) => {
        arquivosPorIndice.set(indice, files[posicao]);
    });
    
    return uploadMultipleFiles({
        files,
        indexes,
        demandaId,
        onProgress: (idx, percent) => {
            setUploadingFiles(prev => 
                prev.map((f, i) => 
                    i === idx ? { ...f, progress: percent, progresso: percent } : f
                )
            );
        },
        onSuccess: (idx, responseData) => {
            setUploadingFiles(prev => 
                prev.map((f, i) => {
                    if (i !== idx) return f;
                    return {
                        ...f,
                        status: 'success',
                        progress: 100,
                        progresso: 100,
                        erro: undefined
                    };
                })
            );
            
            // Remove o card e chama o callback quando removido
            removeFileByIndex(setUploadingFiles, idx, removalDelay, () => {
                onSuccessCallback?.(responseData);
            }, arquivosPorIndice.get(idx));
        },
        onError: (idx, errorMessage) => {
            setUploadingFiles(prev => 
                prev.map((f, i) => {
                    if (i !== idx) return f;
                    return { 
                        ...f, 
                        status: 'error', 
                        error: errorMessage,
                        erro: errorMessage
                    };
                })
            );
        }
    });
};

export const removeSuccessfulFiles = (
    setUploadingFiles: React.Dispatch<React.SetStateAction<FileUploadInfo[]>>,
    delay: number = SUCCESS_REMOVAL_DELAY
) => {
    setTimeout(() => {
        setUploadingFiles(prev => prev.filter(f => f.status !== 'success'));
    }, delay);
};

export const removeFileByIndex = (
    setUploadingFiles: React.Dispatch<React.SetStateAction<FileUploadInfo[]>>,
    index: number,
    delay: number = SUCCESS_REMOVAL_DELAY,
    onRemoved?: () => void,
    fileReference?: File
) => {
    setTimeout(() => {
        setUploadingFiles(prev => {
            const alvo = fileReference ?? prev[index]?.file;
            if (alvo) {
                return prev.filter((item) => {
                    const arquivoPortugues = (item as any).arquivo;
                    if (arquivoPortugues) {
                        return arquivoPortugues !== alvo;
                    }
                    return item.file !== alvo;
                });
            }
            return prev.filter((_, idx) => idx !== index);
        });
        onRemoved?.();
    }, delay);
};

